/**
 * NetDocuments Iframe Integration Hook
 *
 * Handles the complete ND iframe login flow when the app is loaded inside
 * the NetDocuments iframe with URL params like:
 *   ?from=nd&mode=nd1&pk=ACQ1&pd=Acquisition%20Review&ck=1000&cd=...&un=...&curpage=...
 *
 * Flow (mirrors Angular):
 * 1. Detect `from=nd` in URL → parse & cache ND params, clear WIP filters
 * 2. If not authenticated → open login popup, relay auth data via postMessage
 * 3. On ND auth success → call getDefaultClientDetails, store defaultNdConfig
 * 4. Relay auth data back to parent frame / opener via postMessage
 */

import { useEffect, useRef, useCallback } from 'react';
import { isIframeEnvironment, isAllowedOrigin } from '@/utils/ndEnvironment';
import {
  parseNdParamsFromUrl,
  setCachedNdConfig,
  getCachedNdConfig,
  clearCachedNdConfig,
  setDefaultNdConfig,
  fetchDefaultClientDetails,
  clearWipFiltersForNDFrame,
} from '@/utils/ndConfigManager';
import { ND_AUTH_CHANGED_EVENT } from '@/utils/ndTokenManager';

/**
 * Initialise the ND iframe integration.
 *
 * Call this once at the app root level (e.g., inside AppShell or a top-level
 * layout component that renders after the user is authenticated).
 *
 * @param isAuthenticated Whether the user is authenticated with the motr app
 */
export const useNdIframeIntegration = (isAuthenticated: boolean): void => {
  const initRef = useRef(false);
  const processedCachedConfig = useRef(false);

  // ── Step 1: Parse URL params on mount ──────────────────────────────────
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const ndParams = parseNdParamsFromUrl();
    if (ndParams) {
      clearWipFiltersForNDFrame();
      setCachedNdConfig(ndParams);
    }
  }, []);

  // ── Step 2: Notify opener that auth is complete (popup → parent) ─────
  // When this window was opened as a login popup, notify the opener that
  // authentication succeeded. Since popup and parent share the same origin
  // (and therefore the same localStorage), the oidc-client-ts user data is
  // already available to the parent — it just needs to know when to reload.
  useEffect(() => {
    if (!isAuthenticated || !window.opener) return;

    // Don't auto-close windows that are handling DocuSign callbacks —
    // these are opened via window.open (so window.opener is set) but
    // need to stay open to process the signing/prepare result.
    const path = window.location.pathname;
    if (path.includes('/wip-dashboard/') && path.match(/\/e\/[^/]+\/(signed|sentForSigning)/)) {
      return;
    }

    const timer = setTimeout(() => {
      try {
        const openerOrigin = window.opener?.location?.origin;
        if (!openerOrigin || !isAllowedOrigin(openerOrigin)) {
          console.warn('Invalid opener origin:', openerOrigin);
          return;
        }

        // Send a typed signal so the parent LoginPage knows auth is done.
        // The actual auth state is already in shared localStorage via oidc-client-ts.
        window.opener.postMessage({ type: 'motr:auth-complete' }, openerOrigin);

        // Close this popup window
        try {
          window.close();
        } catch {
          // Some browsers block window.close() for non-script-opened windows
        }
      } catch (err) {
        console.error('Error notifying opener:', err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  // ── Step 3: Listen for auth data from login popup (iframe parent) ──────
  useEffect(() => {
    if (!isIframeEnvironment()) return;

    const handleAuthMessage = (event: MessageEvent) => {
      if (!isAllowedOrigin(event.origin)) return;
      if (!event.data || typeof event.data !== 'object') return;

      const authData = event.data as Record<string, string>;
      if (!authData.access_token) return;

      // Store all auth data from the login popup
      Object.entries(authData).forEach(([key, value]) => {
        if (value) {
          localStorage.setItem(key, value);
        }
      });

      // Reload to pick up the new auth state
      window.location.reload();
    };

    window.addEventListener('message', handleAuthMessage);
    return () => window.removeEventListener('message', handleAuthMessage);
  }, []);

  // ── Step 4: Process cached ND config → call API to get enriched config ──
  const processCachedNdConfig = useCallback(async () => {
    if (processedCachedConfig.current) return;

    const cachedConfig = getCachedNdConfig();
    if (!cachedConfig?.pk) return;

    processedCachedConfig.current = true;

    // Call the API to enrich the URL params into cabinet/client/matter IDs.
    // ND token is passed when available but the endpoint may work without it
    // (it resolves the params server-side using the cached config keys).
    const result = await fetchDefaultClientDetails(cachedConfig);
    if (result) {
      setDefaultNdConfig(result);
      clearCachedNdConfig();
    } else {
      // API call failed — reset flag so we can retry when ND auth arrives
      processedCachedConfig.current = false;
    }
  }, []);

  // Run when motr auth is ready (with a small delay to let the API client
  // token getter be set — both happen in AppShell useEffects on the same render)
  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => processCachedNdConfig(), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, processCachedNdConfig]);

  // Also run when ND auth changes (user just authenticated with ND)
  useEffect(() => {
    const handleNdAuthChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.authenticated && isAuthenticated) {
        // Reset so we can process again
        processedCachedConfig.current = false;
        processCachedNdConfig();
      }
    };

    window.addEventListener(ND_AUTH_CHANGED_EVENT, handleNdAuthChanged);
    return () => window.removeEventListener(ND_AUTH_CHANGED_EVENT, handleNdAuthChanged);
  }, [isAuthenticated, processCachedNdConfig]);

  // ── Step 5: Open login popup when in iframe and not authenticated ──────
  useEffect(() => {
    if (!isIframeEnvironment()) return;
    if (isAuthenticated) return;

    // The AuthGuard handles the normal OIDC redirect, but in an iframe
    // that redirect won't work (X-Frame-Options). Instead, open a popup.
    // This is handled by the AuthGuard's existing login() call which does
    // signinRedirect — for iframe scenarios, the Angular app opens a popup
    // via home.component's login() method. The React AuthGuard already
    // redirects to the OIDC provider which works in an iframe context
    // because the OIDC redirect breaks out of the iframe.
    //
    // If the OIDC redirect doesn't work in the iframe, we'd need a popup-
    // based login here. For now, we rely on the existing auth flow.
  }, [isAuthenticated]);
};
